"use client";

import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Calendar,
  CalendarCheck2,
  Clock,
  ArrowLeft,
  MapPin,
  Phone,
  Ticket,
  Music,
  Utensils,
  HelpCircle,
  Share2,
  Flame,
  Trophy,
  Tags,
  CalendarHeart,
  Handshake,
  PartyPopper,
} from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { buildMapsHrefForLocation, getLocationBySlug } from "@/lib/restaurant-platform";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import {
  isLocalPreviewPublicHost,
  publicLocationCookieName,
} from "@/lib/public-location-cookie";
import {
  setBrowserPublicLocationIdOverride,
  useBrowserPublicLocationId,
} from "@/lib/use-browser-public-location-id";
import { parseLocalDate } from "@/lib/utils";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";
import { RSVPModal } from "@/components/events/rsvp-modal";

// Convert 24-hour time string to 12-hour format
function formatTimeTo12Hour(timeString: string): string {
  const [hours, minutes] = timeString.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeString;

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHours}:${displayMinutes} ${period}`;
}

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (normalized.length !== 10) return phone;

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

// Get event type configuration
function getEventTypeConfig(eventType?: string) {
  switch (eventType) {
    case "special_dinner":
      return {
        icon: Utensils,
        label: "Special Dinner",
        color: "bg-rose-500",
      };
    case "trivia":
      return {
        icon: HelpCircle,
        label: "Trivia Night",
        color: "bg-cyan-700",
      };
    case "taco_tuesday":
      return {
        icon: Flame,
        label: "Taco Tuesday",
        color: "bg-lime-700",
      };
    case "watch_party":
      return {
        icon: Trophy,
        label: "Watch Party",
        color: "bg-sky-700",
      };
    case "happy_hour":
      return {
        icon: Tags,
        label: "Happy Hour",
        color: "bg-teal-700",
      };
    case "holiday":
      return {
        icon: CalendarHeart,
        label: "Holiday",
        color: "bg-red-800",
      };
    case "community":
      return {
        icon: Handshake,
        label: "Community",
        color: "bg-indigo-700",
      };
    case "social":
      return {
        icon: PartyPopper,
        label: "Social",
        color: "bg-fuchsia-700",
      };
    case "live_music":
    default:
      return {
        icon: Music,
        label: "Live Music",
        color: "bg-amber-500",
      };
  }
}

function resolveEventLocation(locationId?: string) {
  if (!locationId) return null;

  try {
    return getLocationBySlug(locationId);
  } catch {
    return null;
  }
}

type EventDetailClientProps = {
  initialLocationId: string;
};

export function EventDetailClient({ initialLocationId }: EventDetailClientProps) {
  const params = useParams();
  const slug = params.slug as string;
  // Host-aware fallback brand/location. When the event has no resolvable
  // location, the displayed name/address/phone fall back to this host's tenant
  // (resolved from the request via the marketing context) rather than the
  // build-time env tenant, so Roma's host never shows Tuscan fallbacks.
  const { siteConfig } = useMarketingLocationContext();
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const publicLocationId = useBrowserPublicLocationId(initialLocationId);
  const event = useQuery(api.events.getBySlug, {
    slug,
    locationId: publicLocationId,
  });
  const upcomingEvents = useQuery(
    api.events.getUpcoming,
    event ? { limit: 3, locationId: event.locationId } : "skip",
  );
  const rsvpAvailability = useQuery(
    api.eventRsvps.getAvailability,
    event?.status === "published"
      ? { eventId: event._id, locationId: event.locationId }
      : "skip",
  );
  const eventLocation = resolveEventLocation(event?.locationId);

  useEffect(() => {
    if (!event?.locationId || !eventLocation) return;

    setBrowserPublicLocationIdOverride(event.locationId);

    const pageBrandElement = document.querySelector<HTMLElement>(
      "[data-public-page-brand]",
    );
    const previousPageBrand = pageBrandElement?.getAttribute(
      "data-public-page-brand",
    );
    const pageBrand = getPublicPageBrand({ name: eventLocation.name });

    pageBrandElement?.setAttribute("data-public-page-brand", pageBrand);

    if (isLocalPreviewPublicHost(window.location.origin)) {
      document.cookie = `${publicLocationCookieName}=${encodeURIComponent(
        event.locationId,
      )}; path=/; max-age=${60 * 60 * 24 * 7}; samesite=lax`;
    }

    return () => {
      setBrowserPublicLocationIdOverride(undefined);

      if (!pageBrandElement) return;

      if (previousPageBrand) {
        pageBrandElement.setAttribute("data-public-page-brand", previousPageBrand);
      } else {
        pageBrandElement.removeAttribute("data-public-page-brand");
      }
    };
  }, [event?.locationId, eventLocation]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.title,
          text: event?.description,
          url: window.location.href,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  // Loading state
  if (event === undefined) {
    return (
      <div className="roma-paper min-h-screen animate-pulse">
        <div className="h-64 border-b border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/55 sm:h-80" />
        <div className="container mx-auto max-w-5xl space-y-5 px-4 py-10 sm:px-6 sm:py-14">
          <div className="h-4 w-32 rounded bg-[var(--roma-linen)]" />
          <div className="h-12 w-3/4 rounded bg-[var(--roma-linen)]" />
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="h-28 rounded bg-[var(--roma-linen)]" />
            <div className="h-28 rounded bg-[var(--roma-linen)]" />
          </div>
          <div className="space-y-3 pt-4">
            <div className="h-4 w-full rounded bg-[var(--roma-linen)]" />
            <div className="h-4 w-full rounded bg-[var(--roma-linen)]" />
            <div className="h-4 w-2/3 rounded bg-[var(--roma-linen)]" />
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (event === null) {
    return (
      <div className="roma-paper flex min-h-[70vh] items-center px-4 py-20 sm:px-6">
        <div className="mx-auto w-full max-w-xl border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] p-6 text-left shadow-[0_24px_70px_-52px_oklch(0.24_0.03_55_/_0.35)] sm:p-8">
          <div className="roma-icon-chip mb-6 flex h-16 w-16 items-center justify-center rounded-full">
            <Calendar className="h-8 w-8" />
          </div>
          <p className="roma-eyebrow text-[var(--roma-terracotta)]">
            <span>Events</span>
          </p>
          <h1 className="mt-4 font-serif text-3xl font-normal tracking-tight text-[var(--foreground)]">
            Event not found.
          </h1>
          <p className="roma-text-muted mt-3 max-w-md leading-7">
            This event may have ended or the link is incorrect. Check out our upcoming events below.
          </p>
          <Button asChild className="roma-cta mt-6 rounded-full">
            <Link href="/events">View All Events</Link>
          </Button>
        </div>
      </div>
    );
  }

  const eventDate = parseLocalDate(event.date);
  const typeConfig = getEventTypeConfig(event.eventType);
  const TypeIcon = typeConfig.icon;
  const locationName = eventLocation ? eventLocation.name : siteConfig.name;
  const locationAddressLine1 = eventLocation
    ? eventLocation.address.line1
    : siteConfig.address.line1;
  const locationAddressCity = eventLocation
    ? eventLocation.address.city
    : siteConfig.address.city;
  const locationAddressState = eventLocation
    ? eventLocation.address.state
    : siteConfig.address.state;
  const locationAddressZip = eventLocation
    ? eventLocation.address.zip
    : siteConfig.address.zip;
  const locationPhone = eventLocation ? eventLocation.contact.phone : siteConfig.contact.phone;
  const locationReservationUrl = eventLocation
    ? eventLocation.reservationUrl
    : siteConfig.reservationUrl;
  const locationMapsHref = eventLocation
    ? buildMapsHrefForLocation(eventLocation)
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state}`,
      )}`;

  // Format date parts
  const dayName = eventDate.toLocaleDateString("en-US", { weekday: "long" });
  const monthDay = eventDate.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  const year = eventDate.getFullYear();

  // Check if event is in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPast = eventDate < today;
  const isRsvpEligible = event.status === "published";
  const isRsvpAvailabilityLoading = isRsvpEligible && rsvpAvailability === undefined;
  const remainingGuests = rsvpAvailability?.remainingGuests;
  const hasRsvpCapacityLimit = remainingGuests !== undefined;
  const isRsvpFull = rsvpAvailability?.isFull ?? false;
  const maxPartySize = hasRsvpCapacityLimit ? Math.min(Math.max(remainingGuests, 0), 10) : 10;
  const isRsvpDisabled = isRsvpAvailabilityLoading || isRsvpFull;
  const rsvpButtonLabel = isRsvpAvailabilityLoading
    ? "Checking Availability"
    : isRsvpFull
      ? "Guest List Full"
      : "Join the Guest List";
  const rsvpStatusText = isRsvpAvailabilityLoading
    ? "Checking guest-list availability."
    : isRsvpFull
      ? "The guest list is full."
      : hasRsvpCapacityLimit
        ? `${remainingGuests} ${remainingGuests === 1 ? "spot" : "spots"} available.`
        : "The guest list is open.";
  const rsvpHelpText = isRsvpAvailabilityLoading
    ? "We're checking the guest list before opening RSVPs."
    : isRsvpFull
      ? "Call us if you want to ask about cancellations."
      : "Add your party size and we'll email a confirmation. Tables remain first come, first served.";
  const stickyRsvpStatus = isRsvpAvailabilityLoading
    ? "Checking availability"
    : isRsvpFull
      ? "Guest list is currently full"
      : remainingGuests !== undefined && remainingGuests <= 10
        ? `Only ${remainingGuests} ${remainingGuests === 1 ? "spot" : "spots"} left`
        : "Guest list is open";
  const locationPhoneDisplay = formatPhoneDisplay(locationPhone);

  const handleRsvpClick = (source: "event_detail" | "event_detail_mobile") => {
    if (isRsvpDisabled) return;
    analytics.reservationClicked(source, {
      location: event.locationId,
      cta_text: rsvpButtonLabel,
      lead_type: "event_rsvp",
    });
    analytics.eventRsvpStarted(event.title, {
      event_id: event._id,
      event_type: event.eventType,
      location_id: event.locationId,
      source,
    });
    setRsvpOpen(true);
  };

  const trackPhoneClick = (source: string, ctaText: string) => {
    analytics.phoneClicked({
      source,
      location: event.locationId,
      cta_text: ctaText,
      destination_url: `tel:${locationPhone}`,
    });
  };

  // Filter out current event from related events
  const relatedEvents = upcomingEvents?.filter((e) => e._id !== event._id).slice(0, 2);

  return (
    <div className="roma-paper min-h-screen pb-[var(--sticky-bar-clearance)] md:pb-20">
      <section className="relative overflow-hidden border-b border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]">
        <div className="absolute inset-0 hidden sm:block">
          {event.imageUrl ? (
            <>
              {/* Event images can come from Convex storage or an external image host. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeCloudinaryUrl(event.imageUrl, 1600)}
                alt={event.title}
                className="h-full w-full object-cover"
              />
            </>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[var(--roma-espresso)]">
              <TypeIcon className="h-24 w-24 text-[var(--roma-ivory)]/12" />
            </div>
          )}
          <div className="absolute inset-0 bg-[var(--roma-espresso)]/78" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-6 sm:px-6 sm:py-14 md:py-16">
          <div className="mb-5 flex items-center justify-between gap-3 sm:mb-8">
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 rounded-full border-[var(--roma-parchment-line)] bg-transparent px-4 text-[var(--foreground)] hover:bg-[var(--roma-linen)] sm:min-h-9 sm:border-white/30 sm:text-white sm:hover:bg-white/10"
              asChild
            >
              <Link href="/events">
                <ArrowLeft className="mr-2 h-4 w-4" /> All Events
              </Link>
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="min-h-11 min-w-11 rounded-full border-[var(--roma-parchment-line)] bg-transparent text-[var(--foreground)] hover:bg-[var(--roma-linen)] sm:min-h-9 sm:min-w-9 sm:border-white/30 sm:text-white sm:hover:bg-white/10"
              onClick={handleShare}
              aria-label="Share event"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(18rem,0.42fr)] md:items-end md:gap-8">
            <div className="max-w-3xl space-y-4 sm:space-y-5">
              <p className="inline-flex items-center gap-2 border-y border-[var(--roma-terracotta)]/30 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--roma-terracotta)] sm:gap-3 sm:border-white/25 sm:py-2 sm:tracking-[0.24em] sm:text-[var(--roma-saffron)]">
                <TypeIcon className="h-3.5 w-3.5" />
                {typeConfig.label}
              </p>
              <div className="space-y-3 sm:space-y-4">
                <h1 className="font-serif text-3xl font-normal leading-tight tracking-tight text-[var(--foreground)] sm:text-5xl sm:leading-none sm:text-white md:text-6xl">
                  {event.title}
                </h1>
                {event.description && (
                  <p className="line-clamp-3 max-w-2xl text-sm leading-6 text-[var(--foreground)]/72 sm:line-clamp-none sm:text-lg sm:leading-8 sm:text-white/82">
                    {event.description}
                  </p>
                )}
              </div>
            </div>

            {event.imageUrl && (
              <div className="overflow-hidden border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] shadow-[0_24px_70px_-52px_oklch(0.24_0.03_55_/_0.45)] sm:hidden md:block">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={optimizeCloudinaryUrl(event.imageUrl, 720)}
                  alt={event.title}
                  className="block h-auto w-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="container relative z-10 mx-auto max-w-5xl px-4 py-5 sm:px-6 sm:py-12 md:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] shadow-[0_24px_70px_-56px_oklch(0.24_0.03_55_/_0.35)]">
            <CardContent className="p-4 sm:p-7 md:p-9">
              {/* Badges */}
              <div className="mb-4 flex flex-wrap gap-2 sm:mb-6">
                <Badge
                  variant="outline"
                  className="rounded-full border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 px-3 py-1 text-[var(--foreground)]/72"
                >
                  <TypeIcon className="mr-1 h-3 w-3 text-[var(--roma-terracotta)]" />
                  {typeConfig.label}
                </Badge>
                {event.isVip && (
                  <Badge className="rounded-full border border-[var(--roma-saffron)]/45 bg-[var(--roma-saffron)]/18 px-3 py-1 text-[var(--foreground)] shadow-none">
                    VIP Event
                  </Badge>
                )}
                {isPast && (
                  <Badge
                    variant="outline"
                    className="rounded-full border-[var(--roma-parchment-line)] bg-transparent px-3 py-1 text-[var(--foreground)]/58"
                  >
                    Past Event
                  </Badge>
                )}
              </div>

              {/* Title */}
              <h2 className="mb-6 hidden font-serif text-3xl font-normal leading-tight tracking-tight text-[var(--foreground)] sm:block sm:text-4xl md:text-5xl">
                {event.title}
              </h2>

              {/* Date & Time Info */}
              <div className="mb-5 grid grid-cols-2 gap-2 sm:mb-8 sm:gap-4">
                <div className="flex items-start gap-2 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-3 sm:gap-3 sm:p-4">
                  <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-[var(--roma-terracotta)] sm:h-5 sm:w-5" />
                  <div>
                    <p className="text-sm font-semibold leading-tight text-[var(--foreground)] sm:text-base">
                      {dayName}
                    </p>
                    <p className="text-sm text-[var(--foreground)]/62">
                      {monthDay}, {year}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-3 sm:gap-3 sm:p-4">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[var(--roma-terracotta)] sm:h-5 sm:w-5" />
                  <div>
                    <p className="text-sm font-semibold leading-tight text-[var(--foreground)] sm:text-base">
                      {formatTimeTo12Hour(event.startTime)}
                    </p>
                    {event.endTime && (
                      <p className="text-sm text-[var(--foreground)]/62">
                        Until {formatTimeTo12Hour(event.endTime)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {isRsvpEligible && (
                <div
                  id="rsvp"
                  className="mb-5 scroll-mt-24 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 p-4 sm:mb-8 sm:p-5"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--roma-terracotta)] sm:text-sm">
                    RSVP list
                  </p>
                  <p className="mt-1.5 font-serif text-xl font-normal text-[var(--foreground)] sm:mt-2 sm:text-2xl">
                    {rsvpStatusText}
                  </p>
                  <p className="roma-text-muted mt-1.5 text-sm leading-6 sm:mt-2 sm:text-base sm:leading-7">
                    {rsvpHelpText}
                  </p>
                </div>
              )}

              {/* Pricing for special dinners */}
              {event.eventType === "special_dinner" &&
                (event.pricePerPerson || event.menuHighlight) && (
                  <div className="mb-5 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 p-4 sm:mb-8 sm:p-5">
                    {event.pricePerPerson && (
                      <p className="mb-1 font-serif text-xl font-normal text-[var(--foreground)] sm:text-2xl">
                        ${event.pricePerPerson} per person
                      </p>
                    )}
                    {event.menuHighlight && (
                      <p className="roma-text-muted text-sm leading-6 sm:text-base sm:leading-7">
                        {event.menuHighlight}
                      </p>
                    )}
                  </div>
                )}

              {/* Ticket pricing */}
              {(event.ticketPrice ?? 0) > 0 && !event.pricePerPerson && (
                <div className="mb-5 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 p-4 sm:mb-8 sm:p-5">
                  <div className="flex items-center gap-2">
                    <Ticket className="h-5 w-5 text-[var(--roma-terracotta)]" />
                    <p className="font-serif text-2xl font-normal text-[var(--foreground)]">
                      ${event.ticketPrice}
                    </p>
                  </div>
                </div>
              )}

              {/* Description */}
              {event.description && (
                <div className="mb-5 sm:mb-8">
                  <h3 className="mb-2 font-serif text-xl font-normal text-[var(--foreground)] sm:mb-3 sm:text-2xl">
                    About this event
                  </h3>
                  <p className="roma-text-muted whitespace-pre-wrap text-sm leading-6 sm:text-base sm:leading-7">
                    {event.description}
                  </p>
                </div>
              )}

              {/* Location */}
              <div className="mb-5 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-4 sm:mb-8 sm:p-5">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[var(--roma-terracotta)]" />
                  <div>
                    <p className="font-semibold text-[var(--foreground)]">{locationName}</p>
                    <p className="text-sm text-[var(--foreground)]/62">{locationAddressLine1}</p>
                    <p className="text-sm text-[var(--foreground)]/62">
                      {locationAddressCity}, {locationAddressState} {locationAddressZip}
                    </p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <a
                    href={locationMapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-full border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/55 px-3 text-sm font-semibold text-[var(--foreground)] transition-colors hover:bg-[var(--roma-linen)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--roma-terracotta)] focus-visible:ring-offset-2"
                    aria-label={`Get directions to ${locationName}`}
                    onClick={() =>
                      analytics.directionsClicked({
                        source: "event_detail",
                        location: event.locationId,
                        cta_text: "Directions",
                        destination_url: locationMapsHref,
                      })
                    }
                  >
                    <MapPin className="h-4 w-4 text-[var(--roma-terracotta)]" />
                    Directions
                  </a>
                  <a
                    href={`tel:${locationPhone}`}
                    className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-full border border-[var(--roma-parchment-line)] bg-transparent px-3 text-sm font-semibold text-[var(--foreground)] transition-colors hover:bg-[var(--roma-linen)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--roma-terracotta)] focus-visible:ring-offset-2"
                    aria-label={`Call ${locationName} at ${locationPhoneDisplay}`}
                    onClick={() => trackPhoneClick("event_detail_location", "Call Us")}
                  >
                    <Phone className="h-4 w-4 text-[var(--roma-terracotta)]" />
                    Call Us
                  </a>
                </div>
              </div>

              {/* Desktop primary CTA. Mobile uses the single sticky CTA below. */}
              {!isPast && (
                <div className="hidden md:flex">
                  {isRsvpEligible ? (
                    isRsvpFull ? (
                      <Button
                        size="lg"
                        variant="outline"
                        className="h-14 flex-1 rounded-full border-[var(--roma-parchment-line)] bg-transparent text-base font-semibold text-[var(--foreground)] hover:bg-[var(--roma-linen)]"
                        asChild
                      >
                        <a
                          href={`tel:${locationPhone}`}
                          aria-label={`Call ${locationName} about event cancellations at ${locationPhoneDisplay}`}
                          onClick={() =>
                            trackPhoneClick(
                              "event_detail_cancellations",
                              "Call About Cancellations",
                            )
                          }
                        >
                          <Phone className="h-5 w-5" />
                          Call About Cancellations
                        </a>
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        className="roma-cta h-14 flex-1 rounded-full text-base font-semibold shadow-lg shadow-black/10 transition-transform active:translate-y-px"
                        disabled={isRsvpDisabled}
                        onClick={() => handleRsvpClick("event_detail")}
                      >
                        <CalendarCheck2 className="h-5 w-5" />
                        {rsvpButtonLabel}
                      </Button>
                    )
                  ) : (
                    <Button
                      size="lg"
                      className="roma-cta h-14 flex-1 rounded-full text-base font-semibold shadow-lg shadow-black/10 transition-transform active:translate-y-px"
                      asChild
                    >
                      <a
                        href={event.ticketLink || locationReservationUrl}
                        target={event.ticketLink ? "_blank" : undefined}
                        rel={event.ticketLink ? "noopener" : undefined}
                        onClick={() => analytics.reservationClicked("event_detail")}
                      >
                        {event.ticketLink ? (
                          <>
                            <Ticket className="h-5 w-5" />
                            Get Tickets
                          </>
                        ) : (
                          <>
                            <Calendar className="h-5 w-5" />
                            Reserve a Table
                          </>
                        )}
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Related Events */}
          {relatedEvents && relatedEvents.length > 0 && (
            <div className="mt-8 sm:mt-12">
              <p className="roma-eyebrow text-[var(--roma-terracotta)]">
                <span>More Nights</span>
              </p>
              <h2 className="mb-4 mt-3 font-serif text-2xl font-normal tracking-tight text-[var(--foreground)] sm:mb-6 sm:mt-4 sm:text-4xl">
                More upcoming events.
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                {relatedEvents.map((relatedEvent) => {
                  const relatedDate = parseLocalDate(relatedEvent.date);
                  const relatedTypeConfig = getEventTypeConfig(relatedEvent.eventType);
                  const RelatedIcon = relatedTypeConfig.icon;

                  return (
                    <Link
                      key={relatedEvent._id}
                      href={relatedEvent.slug ? `/events/${relatedEvent.slug}` : "/events"}
                      className="group"
                    >
                      <Card className="h-full border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] shadow-none transition-all hover:bg-white/55">
                        <CardContent className="p-4 sm:p-5">
                          <div className="flex items-start gap-4">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]">
                              {relatedEvent.imageUrl ? (
                                <>
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={optimizeCloudinaryUrl(relatedEvent.imageUrl, 112)}
                                    alt={relatedEvent.title}
                                    className="h-full w-full object-cover saturate-[0.92]"
                                  />
                                </>
                              ) : (
                                <RelatedIcon className="h-6 w-6 text-[var(--roma-terracotta)]" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-1 font-serif text-lg font-normal text-[var(--foreground)] transition-colors group-hover:text-[var(--roma-terracotta)]">
                                {relatedEvent.title}
                              </p>
                              <p className="text-sm text-[var(--foreground)]/58">
                                {relatedDate.toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </p>
                              <Badge
                                variant="outline"
                                className="mt-2 rounded-full border-[var(--roma-parchment-line)] bg-transparent text-xs text-[var(--foreground)]/62"
                              >
                                {relatedTypeConfig.label}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-6 text-left">
                <Button
                  variant="outline"
                  asChild
                  className="rounded-full border-[var(--roma-parchment-line)] bg-transparent text-[var(--foreground)] hover:bg-[var(--roma-linen)]"
                >
                  <Link href="/events">View All Events</Link>
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Mobile Sticky CTA */}
      {!isPast && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]/94 px-4 pb-[var(--sticky-bar-pb)] pt-2.5 shadow-[0_-14px_34px_-24px_oklch(0.24_0.03_55_/_0.45)] backdrop-blur-xl md:hidden">
          <div className="mx-auto max-w-lg">
            {isRsvpEligible ? (
              <>
                <p
                  aria-live="polite"
                  className="mb-2 flex items-center justify-center gap-2 text-center text-xs font-semibold text-[var(--foreground)]/68"
                >
                  <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 rounded-full ${
                      isRsvpAvailabilityLoading
                        ? "bg-amber-400"
                        : isRsvpFull
                          ? "bg-zinc-400"
                          : "bg-emerald-500"
                    }`}
                  />
                  {stickyRsvpStatus}
                </p>
                {isRsvpFull ? (
                  <Button
                    variant="outline"
                    className="h-14 w-full rounded-full border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/55 text-base font-semibold text-[var(--foreground)] shadow-sm"
                    asChild
                  >
                    <a
                      href={`tel:${locationPhone}`}
                      aria-label={`Call ${locationName} about event cancellations at ${locationPhoneDisplay}`}
                      onClick={() =>
                        trackPhoneClick(
                          "event_detail_mobile_cancellations",
                          "Call About Cancellations",
                        )
                      }
                    >
                      <Phone className="h-5 w-5" />
                      Call About Cancellations
                    </a>
                  </Button>
                ) : (
                  <Button
                    className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15 transition-transform active:translate-y-px"
                    disabled={isRsvpDisabled}
                    onClick={() => handleRsvpClick("event_detail_mobile")}
                  >
                    <CalendarCheck2 className="h-5 w-5" />
                    {rsvpButtonLabel}
                  </Button>
                )}
              </>
            ) : (
              <Button
                className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15 transition-transform active:translate-y-px"
                asChild
              >
                <a
                  href={event.ticketLink || locationReservationUrl}
                  target={event.ticketLink ? "_blank" : undefined}
                  rel={event.ticketLink ? "noopener" : undefined}
                  onClick={() => analytics.reservationClicked("event_detail_mobile")}
                >
                  {event.ticketLink ? "Get Tickets" : "Reserve a Table"}
                </a>
              </Button>
            )}
          </div>
        </div>
      )}
      {isRsvpEligible && (
        <RSVPModal
          event={event}
          maxPartySize={maxPartySize}
          open={rsvpOpen}
          onOpenChange={setRsvpOpen}
        />
      )}
    </div>
  );
}
